import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  computed,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';

import { ToastModule } from 'primeng/toast';
import { Observable, catchError, of, tap, map, switchMap } from 'rxjs';

import {
  SKUItem,
  SKUDetail,
  SKUAttribute,
  DropdownOption,
  TreeNode,
  SKUType,
} from '../../models/sku.interface';
import { SKUService } from '../../services/sku.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { BarcodeDialogComponent } from '../barcode-dialog/barcode-dialog.component';
import { BarcodeTableComponent } from '../barcode-table/barcode-table.component';
import { DataTableComponent, TableColumn } from '@shared/components/data-table/data-table.component';
import { FormControl } from '@angular/forms';
import { Barcode } from '../../models/sku.interface';
import { SpuControllerService } from '@app/api/api/spuController.service';
import { GoodsSkuControllerService } from '@app/api/api/goodsSkuController.service';
import { SkuBatchSaveDTO } from '@app/api/model/skuBatchSaveDTO';
import { GoodsPageReqDTO } from '@app/api/model/goodsPageReqDTO';
import { GoodsPageResVO } from '@app/api/model/goodsPageResVO';
import { PageInfoGoodsPageResVO } from '@app/api/model/pageInfoGoodsPageResVO';
import { GoodsDetailsVO } from '@app/api/model/goodsDetailsVO';
import { AttrListRequestDTO } from '@app/api/model/attrListRequestDTO';
import {
  GOODS_ATTRIBUTE_SOURCES_JSON,
  ATTRIBUTE_SECTION_LABELS,
  AttributeSourceType,
} from '../../models/sku.constants';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import { TaxCategoryService } from '@app/feature/configuration/product/tax-category/services/tax-category.service';
import { ProductTaxControlComponent } from '../../../shared/product-tax/product-tax-control.component';
import {
  ProductTaxInheritedValue,
  ProductTaxOption,
  ProductTaxValue,
} from '../../../shared/product-tax/product-tax.model';
import {
  DEFAULT_PRODUCT_TAX_VALUE,
  deriveProductTaxMode,
  normalizeProductTaxSubmit,
} from '../../../shared/product-tax/product-tax.util';
import { SkuTaxResolutionService } from '../../../shared/product-tax/sku-tax-resolution.service';
import { PermissionService } from '@app/core/services/permission.service';
import {
  GoodsSkuControllerService as GoodsFieldMetadataControllerService,
  ProductFieldVO,
} from '@app/api/goods-service';

// Submission types for attribute rendering - matching API values
export const SUBMISSIONS_TYPES = {
  RADIO_BUTTON: 0, // API: 0-Radio
  CHECKBOX: 1, // API: 1-Checkbox
  TEXT_BOX: 2, // API: 2-Text
  DROPDOWN: 3, // API: 3-Dropdown
} as const;

export interface AttributeGroup {
  sourceType: AttributeSourceType;
  label: string;
  attributes: ProductAttribute[];
}

export interface ProductInfo {
  id: number;
  spuCode: string;
  spuName: string;
  categoryName: string;
  brandName: string;
  materialId: number;
  materialName: string;
  mainImage: string[];
  imageList: string[];
  attributeList: ProductAttribute[];
  attributeGroups: AttributeGroup[];
  // Additional fields from API response
  name?: string;
  type?: number;
  setFlag?: number;
  categoryId?: number;
  brandId?: number;
  lastUpdatedBy?: string;
  lastUpdateDate?: string;
  createdBy?: string;
  createDate?: string;
  objectVersionNumber?: number;
  status?: number;
  remark?: string;
  taxId?: number | null;
  taxCode?: string | null;
  isPriceIncludeVAT?: boolean | null;
}

export interface ProductAttribute {
  id: string;
  name: string;
  code: string;
  submissionType: number; // Using submissionType from API
  skuFlag: boolean;
  source: number;
  attributeValueList: AttributeValue[];
}

export interface AttributeValue {
  name: string;
  value: string;
}

export interface SKUCreateFormData {
  skuName: string;
  skuCode: string;
  barcodes?: Barcode[]; // Changed from single barcode to barcode list
  weight: number;
  length: number;
  width: number;
  height: number;
  // Additional fields for generated SKUs
  id?: number;
  spuId?: number;
  spuName?: string;
  spuCode?: string;
  status?: number;
  brandName?: string;
  categoryName?: string;
  showFlag?: number;
  setFlag?: number;
  goodsRemark?: string;
  auditIdea?: string;
  skuAttributeList?: any[];
  otherAttributeList?: any[];
  skuDetailList?: any[];
  // Flags for generated SKUs
  isGenerated?: boolean;
  isReadOnly?: boolean;
  taxValue?: ProductTaxValue;
  inheritedTaxValue?: ProductTaxInheritedValue | null;
  [key: string]: any;
}

export interface PackageSkuComponent {
  packageSkuId: number;
  skuCode: string;
  skuName: string;
  spuCode?: string;
  spuName?: string;
  num: number;
}

@Component({
  selector: 'app-sku-create',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    CheckboxModule,
    RadioButtonModule,
    TooltipModule,
    ConfirmDialogModule,
    DialogModule,
    TableModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DividerModule,
    CardModule,
    ToastModule,
    DataTableComponent,
    AppButtonComponent,
    BarcodeDialogComponent,
    BarcodeTableComponent,
    ProductTaxControlComponent,
  ],
  templateUrl: './sku-create.component.html',
  styleUrl: './sku-create.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
})
export class SKUCreateComponent implements OnInit, CanComponentDeactivate {
  readonly dimensionFractionDigits = 3;

  // Make SUBMISSIONS_TYPES available in template
  protected readonly SUBMISSIONS_TYPES = SUBMISSIONS_TYPES;
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly skuService = inject(SKUService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly spuControllerService = inject(SpuControllerService);
  private readonly goodsSkuControllerService = inject(
    GoodsSkuControllerService
  );
  private readonly taxCategoryService = inject(TaxCategoryService);
  private readonly skuTaxResolutionService = inject(SkuTaxResolutionService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly permissionService = inject(PermissionService);
  private readonly goodsFieldMetadataControllerService = inject(
    GoodsFieldMetadataControllerService
  );

  // State signals
  loading = signal(false);
  saving = signal(false);
  skuType = signal<SKUType>('ordinary');
  private skipCanDeactivate = false;
  selectedProduct = signal<ProductInfo | null>(null);
  showProductSelector = signal(false);
  skuList = signal<SKUCreateFormData[]>([]);
  selectedSkus = signal<SKUCreateFormData[]>([]);
  generatedSkus = signal<SKUCreateFormData[]>([]);
  showSkuTable = signal(false);

  // Separate signals for different types of SKUs
  apiGeneratedSkus = signal<SKUCreateFormData[]>([]);
  manuallyGeneratedSkus = signal<SKUCreateFormData[]>([]);
  showApiSkusSection = signal(false);
  showManualSkusSection = signal(false);
  showExistingSkus = signal(true); // Control visibility of existing SKUs section

  // Track expand/collapse state for toggle buttons
  allRowsExpanded = signal(false);

  // Bulk apply confirmation dialog state
  showBulkApplyDialog = signal(false);
  bulkApplyFieldName = signal('');
  bulkApplyValue = signal<number | string | null>(null);
  bulkApplyProperty = signal('');
  bulkApplyCount = signal(0);

  // SKU selection for bulk apply
  showSkuSelectionDialog = signal(false);
  bulkApplySelectedSkus = signal<SKUCreateFormData[]>([]);
  bulkApplyAllSkusSelected = signal(false);
  currentEditingSku = signal<SKUCreateFormData | null>(null);

  // Product selection
  productOptions = signal<ProductInfo[]>([]);
  taxOptions = signal<ProductTaxOption[]>([]);
  selectedProductAttributes = signal<{ [key: string]: FormControl }>({});

  // Separate state for radio button values to avoid interference with checkboxes
  radioButtonValues = signal<{ [key: string]: string }>({});

  // Product selection modal
  productTableLoading = signal(false);
  selectedProductForTable = signal<ProductInfo | null>(null);
  productSearchForm = {
    productCode: '',
    productName: '',
  };

  // Pagination state for product selection
  productCurrentPage = signal(1);
  productPageSize = signal(20);
  productTotalRecords = signal(0);

  // Package SKU component selection
  showPackageComponentSelector = signal(false);
  packageComponentLoading = signal(false);
  packageComponentOptions = signal<PackageSkuComponent[]>([]);
  packageComponentTargetSku = signal<SKUCreateFormData | null>(null);
  selectedPackageComponentsForTable = signal<PackageSkuComponent[]>([]);
  packageComponentSearchForm = {
    skuCode: '',
    skuName: '',
  };
  packageComponentCurrentPage = signal(1);
  packageComponentPageSize = signal(20);
  packageComponentTotalRecords = signal(0);
  selectedPackageComponentsMap = signal<Map<string, PackageSkuComponent[]>>(
    new Map()
  );

  // Row expansion state
  expandedRows = signal<Set<any>>(new Set());

  // Barcode management for each SKU
  // Use skuCode as key instead of object reference for reliable lookup
  skuBarcodeMap = signal<Map<string, Barcode[]>>(new Map());
  selectedBarcodesMap = signal<Map<string, Barcode[]>>(new Map());
  showBarcodeDialog = signal(false);
  editingBarcode = signal<{ sku: SKUCreateFormData; barcode: Barcode | null } | null>(null);
  nextBarcodeId = signal(1);
  
  // Helper to create a computed signal for barcode list of a specific SKU
  getBarcodeListForSkuSignal(sku: SKUCreateFormData) {
    return computed(() => {
      const skuKey = sku.skuCode || sku.id?.toString() || '';
      const map = this.skuBarcodeMap();
      return map.get(skuKey) || [];
    });
  }


  // Form configuration
  tableColumns = computed(() => this.getTableColumns());
  productTableColumns = computed(() => this.getProductTableColumns());
  packageComponentTableColumns = computed(() =>
    this.getPackageComponentTableColumns()
  );

  // Computed properties for template
  selectedProductSkuAttributeCount = computed(() => {
    const product = this.selectedProduct();
    return product ? this.getSkuGeneratingAttributes(product).length : 0;
  });

  // Get all SKUs for saving (both API and manual)
  allSkusForSaving = computed(() => {
    const apiSkus = this.apiGeneratedSkus();
    const manualSkus = this.manuallyGeneratedSkus();
    return [...apiSkus, ...manualSkus];
  });

  // Get available SKUs for bulk apply (excluding the current editing SKU)
  availableSkusForBulkApply = computed(() => {
    const allSkus = this.manuallyGeneratedSkus();
    const currentSku = this.currentEditingSku();

    if (!currentSku) {
      return allSkus;
    }

    // Filter out the current SKU being edited
    return allSkus.filter((sku) => sku !== currentSku);
  });

  // Since filtering is now handled by the API, we just return the products directly
  filteredProductOptions = computed(() => {
    return this.productOptions();
  });

  // Load products specifically for the modal
  private loadProductsForModal(): void {
    this.productTableLoading.set(true);
    this.loadProductsFromAPI().subscribe({
      next: (products) => {
        this.productOptions.set(products);
        this.productTableLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading products for modal:', error);
        this.productOptions.set([]);
        this.productTableLoading.set(false);
      },
    });
  }

  // API method to fetch products
  private loadProductsFromAPI(): Observable<ProductInfo[]> {
    const goodsPageReq: GoodsPageReqDTO = {
      page: this.productCurrentPage() - 1, // API uses 0-based pagination
      size: this.productPageSize(),
      spuCode: this.productSearchForm.productCode || undefined,
      name: this.productSearchForm.productName || undefined,
      status: 1, // Only active products
      setFlag: this.getSpuSetFlagForCurrentFlow(),
    };

    return this.spuControllerService.pageGoods1(goodsPageReq).pipe(
      tap((response: any) => {
        this.productTotalRecords.set(response.totalElements || 0);
      }),
      map((response: any) => {
        // Handle both possible response structures

        const content = response.pageContent;
        const mappedProducts = content.map((item: any) =>
          this.mapApiResponseToProductInfo(item)
        );
        return mappedProducts;
      }),
      catchError((error) => {
        console.error('Error fetching products:', error);
        return of([]);
      })
    );
  }

  // Map API response to ProductInfo interface
  private mapApiResponseToProductInfo(apiItem: any): ProductInfo {
    const mapped = {
      id: apiItem.id || 0,
      spuCode: apiItem.spuCode || '',
      spuName: apiItem.name || '',
      categoryName: apiItem.categoryName || '',
      brandName: apiItem.brandName || '',
      materialId: apiItem.materialId || 0,
      materialName: apiItem.materialName || '',
      mainImage: apiItem.mainImage || [],
      imageList: [], // API doesn't provide imageList, using empty array
      attributeList: [], // API doesn't provide attributeList, using empty array
      attributeGroups: [], // API doesn't provide attributeList, using empty array
      // Additional fields
      name: apiItem.name,
      type: apiItem.type,
      setFlag: apiItem.setFlag,
      categoryId: apiItem.categoryId,
      brandId: apiItem.brandId,
      lastUpdatedBy: apiItem.lastUpdatedBy,
      lastUpdateDate: apiItem.lastUpdateDate,
      createdBy: apiItem.createdBy,
      createDate: apiItem.createDate,
      objectVersionNumber: apiItem.objectVersionNumber,
      status: apiItem.status,
      remark: apiItem.remark,
      taxId: apiItem.taxId ?? null,
      taxCode: apiItem.taxCode ?? null,
      isPriceIncludeVAT: apiItem.isPriceIncludeVAT ?? null,
    };
    return mapped;
  }

  // Load product details with attributes
  private loadProductDetails(productId: number): Observable<ProductInfo> {
    return this.spuControllerService.queryDetail1(productId).pipe(
      map((details: GoodsDetailsVO) => {
        const attributeList = this.mapApiAttributesToProductAttributes(
          details.attrLists || []
        );
        return {
          id: details.id || 0,
          spuCode: details.spuCode || '',
          spuName: details.name || '',
          categoryName: details.categoryName || '',
          brandName: details.brandName || '',
          materialId: details.materialId || 0,
          materialName: details.materialName || '',
          mainImage: details.mainImage || [],
          imageList: details.imageList || [],
          attributeList: attributeList,
          attributeGroups: this.groupAttributesBySource(attributeList),
          // Additional fields
          name: details.name,
          type: details.type,
          setFlag: details.setFlag,
          categoryId: details.categoryId,
          brandId: details.brandId,
          status: details.status,
          remark: details.remark,
          taxId: (details as any).taxId ?? null,
          taxCode: (details as any).taxCode ?? null,
          isPriceIncludeVAT: (details as any).isPriceIncludeVAT ?? null,
        };
      }),
      catchError((error) => {
        console.error('Error loading product details:', error);
        throw error;
      })
    );
  }

  // Map API attributes to ProductAttribute interface
  private mapApiAttributesToProductAttributes(
    apiAttributes: any[]
  ): ProductAttribute[] {
    if (!apiAttributes || !Array.isArray(apiAttributes)) {
      return [];
    }

    return apiAttributes.map((apiAttr, index) => {
      // Map API attribute structure to ProductAttribute interface
      const mappedAttribute: ProductAttribute = {
        id: apiAttr.id || apiAttr.attributeId || `attr_${index}`,
        code: apiAttr.code || apiAttr.attributeCode || `code_${index}`,
        name: apiAttr.name || apiAttr.attributeName || `Attribute ${index}`,
        source: apiAttr.source || apiAttr.attributeSource || 0,
        skuFlag: apiAttr.isSku,
        submissionType:
          apiAttr.submissionsType !== undefined
            ? apiAttr.submissionsType
            : SUBMISSIONS_TYPES.DROPDOWN,
        attributeValueList: apiAttr.values || [],
      };

      return mappedAttribute;
    });
  }

  // Group attributes by their source type
  private groupAttributesBySource(
    attributes: ProductAttribute[]
  ): AttributeGroup[] {
    const groups: { [key: number]: ProductAttribute[] } = {};

    // Initialize groups for all source types
    Object.values(GOODS_ATTRIBUTE_SOURCES_JSON).forEach((sourceType) => {
      groups[sourceType] = [];
    });

    // Group attributes by source type, excluding System Attributes (SYS = 1)
    attributes.forEach((attribute) => {
      const sourceType = attribute.source as AttributeSourceType;
      // Skip System Attributes (SYS = 1) for SKU generation
      if (sourceType === GOODS_ATTRIBUTE_SOURCES_JSON.SYS) {
        return;
      }

      if (groups[sourceType]) {
        groups[sourceType].push(attribute);
      } else {
        // Fallback to SPU if source type is not recognized
        groups[GOODS_ATTRIBUTE_SOURCES_JSON.SPU].push(attribute);
      }
    });

    // Convert to AttributeGroup array, only including groups with attributes
    return Object.entries(groups)
      .filter(([_, attrs]) => attrs.length > 0)
      .map(([sourceType, attrs]) => ({
        sourceType: parseInt(sourceType) as AttributeSourceType,
        label:
          ATTRIBUTE_SECTION_LABELS[
            parseInt(sourceType) as AttributeSourceType
          ] || 'Unknown',
        attributes: attrs,
      }));
  }

  protected isSkuGeneratingAttribute(attribute: ProductAttribute): boolean {
    return (
      Boolean(attribute.skuFlag) &&
      (attribute.submissionType === SUBMISSIONS_TYPES.RADIO_BUTTON ||
        attribute.submissionType === SUBMISSIONS_TYPES.CHECKBOX ||
        attribute.submissionType === SUBMISSIONS_TYPES.DROPDOWN)
    );
  }

  protected getSkuGeneratingAttributeGroups(
    product: ProductInfo | null
  ): AttributeGroup[] {
    if (!product?.attributeGroups?.length) {
      return [];
    }

    return product.attributeGroups
      .map((group) => ({
        ...group,
        attributes: group.attributes.filter((attribute) =>
          this.isSkuGeneratingAttribute(attribute)
        ),
      }))
      .filter((group) => group.attributes.length > 0);
  }

  private getSkuGeneratingAttributes(product: ProductInfo): ProductAttribute[] {
    return this.getSkuGeneratingAttributeGroups(product).flatMap(
      (group) => group.attributes
    );
  }

  // Field metadata for field-level permission gating (see PermissionService.canEditField)
  productFields = signal<ProductFieldVO[]>([]);
  canEditBarcode = computed(() =>
    this.canEditField(this.requiredPermissionFor('barcode'))
  );
  canEditPackageComposition = computed(() =>
    this.canEditField(this.requiredPermissionFor('package_composition'))
  );

  ngOnInit(): void {
    this.skuType.set(this.resolveSkuTypeFromRoute());
    this.loadProducts();
    this.loadTaxOptions();
    this.loadProductFields();
  }

  private loadProductFields(): void {
    this.goodsFieldMetadataControllerService
      .getMappingFields('body', false, { httpHeaderAccept: 'application/json' as any })
      .subscribe({
      next: (fields) => this.productFields.set(fields),
      error: () => this.productFields.set([]),
    });
  }

  private requiredPermissionFor(fieldCode: string): string | null | undefined {
    return this.productFields().find((field) => field.code === fieldCode)
      ?.requiredPermission;
  }

  canEditField(requiredPermission: string | null | undefined): boolean {
    return this.permissionService.canEditField(requiredPermission);
  }

  private loadTaxOptions(): void {
    this.taxCategoryService.getActiveOptions().subscribe({
      next: (options) => this.taxOptions.set(options),
      error: () => this.taxOptions.set([]),
    });
  }

  private getSelectedProductInheritedTaxValue(): ProductTaxInheritedValue | null {
    const product = this.selectedProduct();
    if (!product) {
      return null;
    }

    return {
      taxId: product.taxId ?? null,
      taxCode: product.taxCode ?? null,
      taxRate: null,
      isPriceIncludeVAT: product.isPriceIncludeVAT ?? null,
    };
  }

  isPackageMode(): boolean {
    return this.skuType() === 'package';
  }

  private resolveSkuTypeFromRoute(): SKUType {
    const typeFromRoute = this.route.snapshot.paramMap.get('type');
    const typeFromUrl = this.router.url.includes('/package/')
      ? 'package'
      : this.router.url.includes('/draft/')
        ? 'draft'
        : 'ordinary';
    const type = typeFromRoute || typeFromUrl;
    return type === 'package' || type === 'draft' ? type : 'ordinary';
  }

  private getSpuSetFlagForCurrentFlow(): number {
    return this.skuType() === 'package' ? 1 : 0;
  }

  private loadProducts(): void {
    this.loading.set(true);
    this.loadProductsFromAPI().subscribe({
      next: (products) => {
        this.productOptions.set(products);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading products:', error);
        this.productOptions.set([]);
        this.loading.set(false);
      },
    });
  }

  onSelectProduct(): void {
    this.showProductSelector.set(true);
    this.selectedProductForTable.set(null);
    // Reset search form and load fresh data
    this.productSearchForm.productCode = '';
    this.productSearchForm.productName = '';
    this.productCurrentPage.set(1);
    this.loadProductsForModal();
  }

  onProductSelected(product: ProductInfo): void {
    this.selectedProduct.set(product);
    this.showProductSelector.set(false);
    this.selectedProductForTable.set(null);
    this.initializeAttributeForm();

    // Clear existing generated SKUs and selected SKUs
    this.clearGeneratedSkus();

    // Fetch generated SKUs for the selected product
    this.loadGeneratedSkus(product.id);

    if (this.isPackageMode()) {
      this.createPackageSkuDraft(product);
    }
  }

  /**
   * Clear generated SKUs and reset related state
   */
  private clearGeneratedSkus(): void {
    this.generatedSkus.set([]);
    this.apiGeneratedSkus.set([]);
    this.manuallyGeneratedSkus.set([]);
    this.selectedSkus.set([]);
    this.showSkuTable.set(false);
    this.showApiSkusSection.set(false);
    this.showManualSkusSection.set(false);
    this.expandedRows.set(new Set());
  }

  /**
   * Load generated SKUs for the selected product
   */
  private loadGeneratedSkus(spuId: number): void {
    this.loading.set(true);

    this.goodsSkuControllerService
      .goodsSkuDetail(spuId)
      .pipe(
        switchMap((skus) => {
          const mappedSkus = this.mapGeneratedSkusToFormData(skus);
          const skuCodes = mappedSkus.map((sku) => sku.skuCode).filter(Boolean);
          return this.skuTaxResolutionService.resolveMany(skuCodes).pipe(
            map((resolutions) =>
              mappedSkus.map((sku) => {
                const resolution = resolutions.get(sku.skuCode);
                if (!resolution) return sku;
                const configuredVat = sku.taxValue?.isPriceIncludeVAT ?? null;
                return {
                  ...sku,
                  taxValue: {
                    mode: deriveProductTaxMode(resolution.configuredTaxId, configuredVat),
                    taxId: resolution.configuredTaxId,
                    isPriceIncludeVAT: configuredVat,
                  },
                  inheritedTaxValue: resolution.effective,
                };
              }),
            ),
          );
        }),
        catchError((error) => {
          console.error('Error loading generated SKUs or resolving their tax:', error);
          this.showError(
            this.translateService.instant('goods.tax.messages.resolution_failed'),
          );
          return of([]);
        })
      )
      .subscribe({
        next: (skus) => {
          this.apiGeneratedSkus.set(skus);
          this.showApiSkusSection.set(skus.length > 0);
          this.loading.set(false);

          if (skus.length > 0) {
            this.showSuccess(
              this.translateService.instant(
                'sku_management.messages.loaded_existing_skus',
                { count: skus.length }
              )
            );
          }
        },
        error: (error) => {
          console.error('Error in generated SKUs subscription:', error);
          this.loading.set(false);
        },
      });
  }

  /**
   * Refresh generated SKUs for the currently selected product
   */
  refreshGeneratedSkus(): void {
    const product = this.selectedProduct();
    if (product) {
      this.loadGeneratedSkus(product.id);
    }
  }

  /**
   * Toggle visibility of existing SKUs section
   */
  toggleExistingSkus(): void {
    this.showExistingSkus.update((current) => !current);
  }

  /**
   * Map API response to SKUCreateFormData format for display
   */
  private mapGeneratedSkusToFormData(skus: any[]): SKUCreateFormData[] {
    return skus.map((sku, index) => ({
      id: sku.id,
      skuName: sku.skuName || '',
      skuCode: sku.skuCode || '',
      weight: sku.weight || 0,
      length: sku.length || 0,
      width: sku.width || 0,
      height: sku.height || 0,
      // Additional fields for display
      spuId: sku.spuId,
      spuName: sku.spuName,
      spuCode: sku.spuCode,
      status: sku.status,
      brandName: sku.brandName,
      categoryName: sku.categoryName,
      showFlag: sku.showFlag,
      setFlag: sku.setFlag,
      goodsRemark: sku.goodsRemark,
      auditIdea: sku.auditIdea,
      skuAttributeList: sku.skuAttributeList || [],
      otherAttributeList: sku.otherAttributeList || [],
      skuDetailList: sku.skuDetailList || [],
      taxValue: {
        mode: deriveProductTaxMode(sku.taxId, sku.isPriceIncludeVAT),
        taxId: sku.taxId ?? null,
        isPriceIncludeVAT: sku.isPriceIncludeVAT ?? null,
      },
      inheritedTaxValue: this.getSelectedProductInheritedTaxValue(),
      // Mark as read-only generated SKU
      isGenerated: true,
      isReadOnly: true,
    }));
  }

  private initializeAttributeForm(): void {
    const product = this.selectedProduct();
    if (!product) return;

    const attributes: { [key: string]: FormControl } = {};
    const radioValues: { [key: string]: string } = {};

    this.getSkuGeneratingAttributes(product).forEach((attr) => {
      if (attr.submissionType === SUBMISSIONS_TYPES.CHECKBOX) {
        // Checkboxes use FormControl with array values
        attributes[attr.code] = new FormControl([]);
      } else if (attr.submissionType === SUBMISSIONS_TYPES.RADIO_BUTTON) {
        // Radio buttons use separate signal state
        attributes[attr.code] = new FormControl('');
        radioValues[attr.code] = '';
      } else {
        // Dropdowns use FormControl with string values
        attributes[attr.code] = new FormControl('');
      }
    });

    this.selectedProductAttributes.set(attributes);
    this.radioButtonValues.set(radioValues);
  }

  onGenerateSkus(): void {
    const product = this.selectedProduct();
    const attributes = this.selectedProductAttributes();
    const radioValues = this.radioButtonValues();

    if (!product) {
      this.showError('Please select a product first');
      return;
    }

    if (this.isPackageMode()) {
      this.createPackageSkuDraft(product);
      return;
    }

    const skuAttributes = this.getSkuGeneratingAttributes(product);

    if (skuAttributes.length === 0) {
      this.showError('No SKU attributes found for this product');
      return;
    }

    // Extract values from form controls and radio button signals
    const attributeValues: { [key: string]: any } = {};

    // Process all SKU attributes to ensure we capture both FormControl and radio button values
    skuAttributes.forEach((attr) => {
      if (attr.submissionType === SUBMISSIONS_TYPES.RADIO_BUTTON) {
        // For radio buttons, use the separate signal values
        attributeValues[attr.code] = radioValues[attr.code] || '';
      } else {
        // For checkboxes and dropdowns, use FormControl values
        const control = attributes[attr.code];
        attributeValues[attr.code] = control ? control.value : '';
      }
    });

    // Generate SKU combinations
    const combinations = this.generateCombinations(
      skuAttributes,
      attributeValues
    );

    if (combinations.length === 0) {
      this.showError(
        this.translateService.instant(
          'sku_management.messages.no_valid_combinations'
        )
      );
      return;
    }

    // Get existing SKUs to check for duplicates (both API and manually generated)
    const existingApiSkus = this.apiGeneratedSkus();
    const existingManualSkus = this.manuallyGeneratedSkus();
    const allExistingSkus = [...existingApiSkus, ...existingManualSkus];
    const existingSkuCodes = new Set(allExistingSkus.map((sku) => sku.skuCode));

    // Create a set of existing attribute combinations to check for duplicates
    const existingAttributeCombinations = new Set<string>();
    allExistingSkus.forEach((sku) => {
      const combinationKey = this.createAttributeCombinationKey(
        sku,
        skuAttributes
      );
      if (combinationKey) {
        existingAttributeCombinations.add(combinationKey);
      }
    });

    // Filter out combinations that already exist
    const newCombinations = combinations.filter((combo) => {
      const combinationKey = this.createAttributeCombinationKey(
        combo,
        skuAttributes
      );
      const isDuplicate =
        combinationKey && existingAttributeCombinations.has(combinationKey);
      return (
        combinationKey && !existingAttributeCombinations.has(combinationKey)
      );
    });

    if (newCombinations.length === 0) {
      // Get the duplicate combinations that were filtered out
      const duplicateCombinations = combinations.filter((combo) => {
        const combinationKey = this.createAttributeCombinationKey(
          combo,
          skuAttributes
        );
        return (
          combinationKey && existingAttributeCombinations.has(combinationKey)
        );
      });

      // Show simple, user-friendly error message
      this.showError(
        this.translateService.instant(
          'sku_management.messages.combination_already_exists'
        )
      );
      return;
    }

    const skuList: SKUCreateFormData[] = newCombinations.map((combo, index) => {
      // Generate a suggested SKU code (user can edit this)
      const suggestedSkuCode = this.generateSuggestedSkuCode(
        product,
        combo,
        existingSkuCodes
      );

      // RBA-2273: name = SPU name + abbreviated attrs (not full values / spuCode).
      const skuName = this.generateDescriptiveSkuName(product, combo);

      const newSku: SKUCreateFormData = {
        skuName,
        skuCode: suggestedSkuCode,
        barcodes: [], // Initialize empty barcode list
        // Initialize other system fields
        weight: 0,
        length: 0,
        width: 0,
        height: 0,
        taxValue: { ...DEFAULT_PRODUCT_TAX_VALUE },
        inheritedTaxValue: this.getSelectedProductInheritedTaxValue(),
        // Mark as manually generated
        isGenerated: false,
        isReadOnly: false,
        ...combo,
      };
      
      // Initialize barcode list for this SKU
      const skuKey = newSku.skuCode || newSku.id?.toString() || '';
      this.skuBarcodeMap.update((map) => {
        const newMap = new Map(map);
        newMap.set(skuKey, []);
        return newMap;
      });
      
      return newSku;
    });

    // Add new SKUs to manually generated list
    const updatedManualSkus = [...existingManualSkus, ...skuList];
    this.manuallyGeneratedSkus.set(updatedManualSkus);
    this.showManualSkusSection.set(true);

    // Show success message with information about duplicates
    const skippedCount = combinations.length - newCombinations.length;
    let detailMessage = this.translateService.instant(
      'sku_management.messages.new_skus_generated',
      {
        count: skuList.length,
        total: updatedManualSkus.length,
      }
    );
    if (skippedCount > 0) {
      detailMessage +=
        ' ' +
        this.translateService.instant(
          'sku_management.messages.duplicate_combinations_skipped',
          {
            count: skippedCount,
          }
        );
    }

    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant(
        'sku_management.messages.skus_generated'
      ),
      detail: detailMessage,
      life: 3000,
    });
  }

  private createPackageSkuDraft(product: ProductInfo): void {
    const existingManualSkus = this.manuallyGeneratedSkus();
    const packageSku: SKUCreateFormData = {
      skuName: product.spuName || product.name || '',
      skuCode: this.generatePackageSkuCode(product),
      weight: 0,
      length: 0,
      width: 0,
      height: 0,
      spuId: product.id,
      spuName: product.spuName,
      spuCode: product.spuCode,
      brandName: product.brandName,
      categoryName: product.categoryName,
      showFlag: 1,
      setFlag: 1,
      skuAttributeList: [],
      otherAttributeList: [],
      skuDetailList: [],
      taxValue: { ...DEFAULT_PRODUCT_TAX_VALUE },
      inheritedTaxValue: this.getSelectedProductInheritedTaxValue(),
    };

    this.manuallyGeneratedSkus.set([...existingManualSkus, packageSku]);
    this.showManualSkusSection.set(true);
    this.expandedRows.update((expanded) => new Set([...expanded, packageSku]));
  }

  private generatePackageSkuCode(product: ProductInfo): string {
    if (!product.spuCode) {
      return '';
    }

    const baseCode = `${product.spuCode}-PKG`;
    const existingCodes = new Set(
      [...this.apiGeneratedSkus(), ...this.manuallyGeneratedSkus()]
        .map((sku) => sku.skuCode?.trim())
        .filter((code): code is string => Boolean(code))
    );

    if (!existingCodes.has(baseCode)) {
      return baseCode;
    }

    let suffix = 2;
    while (existingCodes.has(`${baseCode}-${suffix}`)) {
      suffix++;
    }
    return `${baseCode}-${suffix}`;
  }

  private generateCombinations(
    attributes: ProductAttribute[],
    selectedValues: { [key: string]: any }
  ): { [key: string]: any }[] {
    // Filter attributes that have values
    const validAttributes = attributes.filter((attr) => {
      const value = selectedValues[attr.code];
      const isValid = Array.isArray(value)
        ? value.length > 0
        : value && value !== '';
      return isValid;
    });

    if (validAttributes.length === 0) return [];

    const combinations: { [key: string]: any }[] = [];
    const generateCombos = (index: number, current: { [key: string]: any }) => {
      if (index === validAttributes.length) {
        combinations.push({ ...current });
        return;
      }

      const attr = validAttributes[index];
      const values = selectedValues[attr.code];

      if (Array.isArray(values)) {
        // For checkbox/multi-select with values
        values.forEach((value) => {
          current[attr.code] = value;
          generateCombos(index + 1, current);
        });
      } else {
        // For radio button or dropdown with single value
        current[attr.code] = values;
        generateCombos(index + 1, current);
      }
    };

    generateCombos(0, {});
    return combinations;
  }

  /**
   * Creates a unique key for attribute combinations to check for duplicates
   * @param skuData - The SKU data object
   * @param skuAttributes - Array of SKU attributes
   * @returns A unique string key representing the attribute combination
   */
  private createAttributeCombinationKey(
    skuData: any,
    skuAttributes: ProductAttribute[]
  ): string | null {
    if (!skuData || !skuAttributes || skuAttributes.length === 0) {
      return null;
    }

    // Handle API-generated SKUs (which have skuAttributeList)
    if (skuData.isGenerated && skuData.skuAttributeList) {
      const skuAttributeNames = new Set(
        skuAttributes.map((attribute) => attribute.name)
      );
      const attributePairs = skuData.skuAttributeList
        .filter((attr: any) =>
          skuAttributeNames.has(attr.attributeName || attr.name)
        )
        .map((attr: any) => {
          const attributeName = attr.attributeName || attr.name;
          const attributeValue = attr.attributeValue || attr.value;
          return attributeName && attributeValue
            ? `${attributeName}:${attributeValue}`
            : null;
        })
        .filter((pair: string | null) => pair !== null)
        .sort();

      return attributePairs.join('|');
    }

    // Handle manually generated SKUs (which have attributes as direct properties)
    const attributePairs = skuAttributes
      .map((attr) => {
        const value = skuData[attr.code];
        return value ? `${attr.name}:${value}` : null;
      })
      .filter((pair) => pair !== null)
      .sort(); // Sort to ensure consistent key generation

    return attributePairs.join('|');
  }

  /**
   * Suggested SKU name: SPU name + abbreviated selected attributes
   * (`- {first 3 chars of each value}`), not the full skuCode / spec values.
   */
  private generateDescriptiveSkuName(
    product: ProductInfo,
    combo: { [key: string]: any }
  ): string {
    const baseName = (product.spuName || product.name || '').trim();
    const abbreviation = this.getAttributeAbbreviation(combo);
    if (!baseName) {
      return abbreviation ? `- ${abbreviation}` : '';
    }
    if (!abbreviation) {
      return baseName;
    }
    return `${baseName} - ${abbreviation}`;
  }

  /** First 3 characters of each selected attribute value, uppercased and joined. */
  private getAttributeAbbreviation(combo: { [key: string]: any }): string {
    return Object.values(combo)
      .filter((value) => value)
      .map((value) => String(value).substring(0, 3).toUpperCase())
      .join('');
  }

  /**
   * Generates a suggested SKU code (user can edit this)
   * @param product - The selected product
   * @param combo - The attribute combination object
   * @param existingSkuCodes - Set of existing SKU codes to avoid conflicts
   * @returns A suggested unique SKU code
   */
  private generateSuggestedSkuCode(
    product: ProductInfo,
    combo: { [key: string]: any },
    existingSkuCodes: Set<string>
  ): string {
    const attributeSuffix = this.getAttributeAbbreviation(combo);

    let baseCode = `${product.spuCode}-${attributeSuffix}`;

    // If no attributes, use a simple increment
    if (!attributeSuffix) {
      baseCode = `${product.spuCode}-SKU`;
    }

    // Ensure uniqueness by adding a counter if needed
    let suggestedCode = baseCode;
    let counter = 1;

    while (existingSkuCodes.has(suggestedCode)) {
      suggestedCode = `${baseCode}-${counter}`;
      counter++;
    }

    // Add to existing codes to prevent conflicts in the same generation batch
    existingSkuCodes.add(suggestedCode);

    // Ensure the generated code is valid (no special characters)
    if (this.hasSpecialCharacters(suggestedCode)) {
      // If somehow an invalid code was generated, create a safe fallback
      const safeCode = this.sanitizeSkuCode(suggestedCode);
      // Update the existing codes set with the safe version
      existingSkuCodes.delete(suggestedCode);
      existingSkuCodes.add(safeCode);
      return safeCode;
    }

    return suggestedCode;
  }

  onAttributeChange(attributeCode: string, value: any): void {
    const current = this.selectedProductAttributes();
    if (current[attributeCode]) {
      current[attributeCode].setValue(value);
    }
  }

  onSkuSelectionChange(selection: SKUCreateFormData[]): void {
    this.selectedSkus.set(selection);
  }

  onDeleteSelectedSkus(): void {
    const selected = this.selectedSkus();
    if (selected.length === 0) {
      this.showError('Please select SKUs to delete');
      return;
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title'
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.delete_multiple',
        {
          count: selected.length,
        }
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete'
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel'
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        const current = this.manuallyGeneratedSkus();
        const selectedRows = new Set(selected);
        const filtered = current.filter((sku) => !selectedRows.has(sku));
        this.manuallyGeneratedSkus.set(filtered);
        this.selectedSkus.set([]);
      },
    });
  }

  onSaveSkus(isDraft = false): void {
    const skus = this.allSkusForSaving();

    if (skus.length === 0) {
      this.showError('No SKUs to save');
      return;
    }

    // Filter out API-generated SKUs for saving
    const skusToSave = skus.filter((sku) => !sku.isGenerated);

    if (skusToSave.length === 0) {
      this.showError('No manually generated SKUs to save');
      return;
    }

    // Validate that all SKUs have required fields (check trimmed values)
    const invalidSkus = skusToSave.filter(
      (sku) => !sku.skuCode?.trim() || !sku.skuName?.trim()
    );
    if (invalidSkus.length > 0) {
      this.showError(
        'SKU Code and SKU Name are required for all SKUs (cannot be empty or spaces only)'
      );
      return;
    }

    // Validate SKU code format - no special characters allowed
    const skusWithSpecialChars = skusToSave.filter((sku) =>
      this.hasSpecialCharacters(sku.skuCode)
    );
    if (skusWithSpecialChars.length > 0) {
      const invalidSkusList = skusWithSpecialChars
        .map((sku) => `• ${sku.skuCode} (${sku.skuName})`)
        .join('\n');

      this.showError(
        `The following SKU codes contain special characters or exceed length limit:\n${invalidSkusList}\n\nOnly letters, numbers, hyphens, and underscores are allowed (max 32 characters).`
      );
      return;
    }

    if (
      this.isPackageMode() &&
      skusToSave.some(
        (sku) => !sku.skuDetailList || sku.skuDetailList.length === 0
      )
    ) {
      this.showError(
        this.translateService.instant(
          'sku_management.create.package_components.component_required'
        )
      );
      return;
    }

    this.saving.set(true);

    // Use the prepareSavePayload method to get the correct structure
    const skuBatchSaveDTO = this.prepareSavePayload(skusToSave, isDraft);

    this.goodsSkuControllerService.create10(skuBatchSaveDTO as any, 'body', false, {
      httpHeaderAccept: 'application/json' as any
    }).subscribe({
      next: (response) => {
        this.saving.set(false);
        this.skipCanDeactivate = true;
        this.showSuccess(
          this.translateService.instant(
            isDraft
              ? 'sku_management.messages.draft_save_success'
              : this.isPackageMode()
                ? 'sku_management.messages.package_save_success'
                : 'sku_management.messages.ordinary_save_success'
          )
        );
        // Add a small delay to ensure the toast is shown before navigation
        setTimeout(() => {
          this.router.navigate([
            '/goods/sku',
            isDraft ? 'draft' : this.skuType(),
          ]);
        }, 1000);
      },
      error: (error) => {
        this.saving.set(false);
        console.error('Error saving SKUs:', error);

        if (error?.error?.errorCode === 'FIELD_PERMISSION_DENIED') {
          const deniedCodes: string[] = Array.isArray(error?.error?.data)
            ? error.error.data
            : [];
          const fieldLabels = deniedCodes
            .map(
              (code) =>
                this.productFields().find((field) => field.code === code)
                  ?.label ?? code
            )
            .join(', ');
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'sku_management.messages.field_permission_denied',
              { fields: fieldLabels }
            ),
            life: 5000,
          });
          return;
        }

        if (error?.error?.errorCode === 'SPEC_EMBEDDED_IN_PRODUCT_TEXT') {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'sku_management.messages.spec_embedded_in_product_text',
            ),
            life: 5000,
          });
          return;
        }

        const apiMessage = error?.error?.message;
        const defaultMessage = this.translateService.instant(
          'sku_management.messages.save_failed'
        );

        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: apiMessage || defaultMessage,
          life: 5000,
        });
      },
    });
  }

  /**
   * Prepares the payload for the Save & Submit API call
   * @param skus - Array of SKUs to save
   * @returns Formatted payload matching SkuBatchSaveDTO structure
   */
  private prepareSavePayload(
    skus: SKUCreateFormData[],
    isDraft = false,
  ): SkuBatchSaveDTO {
    const product = this.selectedProduct();
    if (!product) {
      throw new Error('No product selected');
    }

    const skuList: any[] = skus.map((sku) => {
      // Extract SKU attributes from the dynamic form data
      const skuAttributeList = this.extractSkuAttributes(sku, product);

      // Get barcodes from map or from sku.barcodes
      const skuKey = sku.skuCode || sku.id?.toString() || '';
      const barcodes = this.skuBarcodeMap().get(skuKey) || sku.barcodes || [];
      const taxSubmitValue = normalizeProductTaxSubmit(
        sku.taxValue ?? DEFAULT_PRODUCT_TAX_VALUE,
      );

      return {
        spuId: product.id,
        skuCode: sku.skuCode,
        skuName: sku.skuName,
        weight: sku.weight || 0,
        length: sku.length || 0,
        width: sku.width || 0,
        height: sku.height || 0,
        taxId: taxSubmitValue.taxId,
        isPriceIncludeVAT: taxSubmitValue.isPriceIncludeVAT,
        skuAttributeList: skuAttributeList,
        showFlag: 1, // Add showFlag set to 1
        skuDetailList: this.isPackageMode()
          ? (sku.skuDetailList || []).map((component: PackageSkuComponent) => ({
              packageSkuId: component.packageSkuId,
              num: component.num,
            }))
          : undefined,
        // Map barcode list to API format: { barcodeType, barcodeValue }
        barcodes: barcodes.map((b) => ({
          barcodeType: b.barcodeType,
          barcodeValue: b.barcodeValue,
        })),
      };
    });

    return {
      skuList: skuList,
      flag: isDraft, // true = save as draft, false = submit for approval
      setFlag: this.getSpuSetFlagForCurrentFlow(),
    };
  }

  /**
   * Extracts SKU attributes from the form data
   * @param sku - SKU form data
   * @param product - Selected product information
   * @returns Array of attribute objects
   */
  private extractSkuAttributes(
    sku: SKUCreateFormData,
    product: ProductInfo
  ): any[] {
    const skuAttributeList: any[] = [];

    this.getSkuGeneratingAttributes(product).forEach((attr) => {
      const attributeValue = sku[attr.code];
      if (
        attributeValue !== undefined &&
        attributeValue !== null &&
        attributeValue !== ''
      ) {
        skuAttributeList.push({
          attributeId: parseInt(attr.id) || 0, // Convert to number
          attributeName: attr.name,
          attributeValue: attributeValue,
          attributeType: attr.submissionType, // Add attributeType from the source (which is attr.type)
        });
      }
    });

    return skuAttributeList;
  }

  /**
   * Finds duplicate SKU codes in the SKU list
   * @param skus - Array of SKUs to check
   * @returns Array of duplicate SKU codes
   */
  private findDuplicateSkuCodes(skus: SKUCreateFormData[]): string[] {
    const skuCodeCounts = new Map<string, number>();
    const duplicates: string[] = [];

    skus.forEach((sku) => {
      if (sku.skuCode) {
        const normalizedCode = sku.skuCode.trim().toLowerCase();
        const count = skuCodeCounts.get(normalizedCode) || 0;
        skuCodeCounts.set(normalizedCode, count + 1);

        if (count === 1) {
          // This is the second occurrence
          duplicates.push(sku.skuCode);
        }
      }
    });

    return duplicates;
  }

  onCancel(): void {
    window.history.back();
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.saving() || this.loading()) {
      return true;
    }

    return !this.selectedProduct() && this.manuallyGeneratedSkus().length === 0;
  }

  private getTableColumns(): TableColumn[] {
    const product = this.selectedProduct();
    if (!product) return [];

    const columns: TableColumn[] = [];

    this.getSkuGeneratingAttributes(product).forEach((attr) => {
      columns.push({
        prop: attr.code,
        label: attr.name,
        width: 140,
      });
    });

    // Add standard SKU columns
    columns.push(
      {
        prop: 'skuName',
        label: this.translateService.instant(
          'sku_management.create.table.sku_name'
        ),
        width: 180,
        type: 'custom',
      },
      {
        prop: 'skuCode',
        label: this.translateService.instant(
          'sku_management.create.table.sku_code'
        ),
        width: 150,
        type: 'custom',
      }
    );

    // Add other system fields
    columns.push(
      { prop: 'weight', label: 'Weight', width: 100, type: 'custom' },
      { prop: 'length', label: 'Length', width: 100, type: 'custom' },
      { prop: 'width', label: 'Width', width: 100, type: 'custom' },
      { prop: 'height', label: 'Height', width: 100, type: 'custom' }
    );

    return columns;
  }

  openPackageComponentSelector(sku: SKUCreateFormData): void {
    this.packageComponentTargetSku.set(sku);
    this.selectedPackageComponentsForTable.set([]);
    this.packageComponentSearchForm.skuCode = '';
    this.packageComponentSearchForm.skuName = '';
    this.packageComponentCurrentPage.set(1);
    this.showPackageComponentSelector.set(true);
    this.loadPackageComponentOptions();
  }

  onPackageComponentSearch(): void {
    this.packageComponentCurrentPage.set(1);
    this.loadPackageComponentOptions();
  }

  onPackageComponentSearchReset(): void {
    this.packageComponentSearchForm.skuCode = '';
    this.packageComponentSearchForm.skuName = '';
    this.packageComponentCurrentPage.set(1);
    this.loadPackageComponentOptions();
  }

  closePackageComponentSelector(): void {
    this.showPackageComponentSelector.set(false);
    this.packageComponentTargetSku.set(null);
    this.selectedPackageComponentsForTable.set([]);
    this.packageComponentOptions.set([]);
    this.packageComponentTotalRecords.set(0);
  }

  private loadPackageComponentOptions(): void {
    this.packageComponentLoading.set(true);
    this.goodsSkuControllerService
      .getList3(
        undefined,
        undefined,
        undefined,
        0,
        undefined,
        [3],
        this.packageComponentSearchForm.skuName || undefined,
        this.packageComponentSearchForm.skuCode || undefined,
        this.packageComponentCurrentPage() - 1,
        this.packageComponentPageSize(),
        undefined,
        undefined
      )
      .pipe(
        tap((response: any) => {
          this.packageComponentTotalRecords.set(response.totalElements || 0);
        }),
        map((response: any) =>
          (response.pageContent || []).map((item: any) => ({
            packageSkuId: item.id || 0,
            skuCode: item.skuCode || '',
            skuName: item.skuName || '',
            spuCode: item.spuCode,
            spuName: item.spuName,
            num: 1,
          }))
        ),
        catchError((error) => {
          console.error('Error loading package components:', error);
          this.showError(
            this.translateService.instant(
              'sku_management.create.package_components.load_failed'
            )
          );
          return of([]);
        })
      )
      .subscribe((options) => {
        this.packageComponentOptions.set(options);
        this.packageComponentLoading.set(false);
      });
  }

  private getPackageComponentTableColumns(): TableColumn[] {
    return [
      {
        prop: 'skuCode',
        label: 'sku_management.create.package_components.sku_code',
        width: 160,
        type: 'text',
      },
      {
        prop: 'skuName',
        label: 'sku_management.create.package_components.sku_name',
        width: 220,
        type: 'text',
      },
      {
        prop: 'spuCode',
        label: 'sku_management.create.package_components.spu_code',
        width: 160,
        type: 'text',
      },
      {
        prop: 'spuName',
        label: 'sku_management.create.package_components.spu_name',
        width: 220,
        type: 'text',
      },
    ];
  }

  onPackageComponentPageChange(event: any): void {
    const newPage =
      Math.floor(event.first / this.packageComponentPageSize()) + 1;
    this.packageComponentCurrentPage.set(newPage);
    this.loadPackageComponentOptions();
  }

  onPackageComponentSelectionChange(selection: any): void {
    const selectedItems = (selection as PackageSkuComponent[]) || [];
    this.selectedPackageComponentsForTable.set(selectedItems);
  }

  onPackageComponentSelectionConfirm(): void {
    const targetSku = this.packageComponentTargetSku();
    const selected = this.selectedPackageComponentsForTable();
    if (!targetSku || selected.length === 0) {
      return;
    }

    const currentComponents = (targetSku.skuDetailList || []) as PackageSkuComponent[];
    const existingIds = new Set(
      currentComponents.map((component) => component.packageSkuId)
    );
    const componentsToAdd = selected.filter(
      (component) => !existingIds.has(component.packageSkuId)
    );

    if (componentsToAdd.length === 0) {
      this.showError(
        this.translateService.instant(
          'sku_management.create.package_components.duplicate_child_sku'
        )
      );
      return;
    }

    targetSku.skuDetailList = [
      ...currentComponents,
      ...componentsToAdd.map((component) => ({
        ...component,
        num: 1,
      })),
    ];

    this.clearSelectedPackageComponents(targetSku);
    this.manuallyGeneratedSkus.set([...this.manuallyGeneratedSkus()]);
    this.closePackageComponentSelector();
  }

  addPackageComponent(component: PackageSkuComponent): boolean {
    const targetSku = this.packageComponentTargetSku();
    if (!targetSku) {
      return false;
    }

    const currentComponents = (targetSku.skuDetailList || []) as PackageSkuComponent[];
    if (
      currentComponents.some(
        (item) => item.packageSkuId === component.packageSkuId
      )
    ) {
      this.showError(
        this.translateService.instant(
          'sku_management.create.package_components.duplicate_child_sku'
        )
      );
      return false;
    }

    targetSku.skuDetailList = [
      ...currentComponents,
      {
        ...component,
        num: 1,
      },
    ];
    this.clearSelectedPackageComponents(targetSku);
    this.manuallyGeneratedSkus.set([...this.manuallyGeneratedSkus()]);
    return true;
  }

  getSelectedPackageComponentsForSku(
    sku: SKUCreateFormData
  ): PackageSkuComponent[] {
    return this.selectedPackageComponentsMap().get(this.getSkuSelectionKey(sku)) || [];
  }

  isPackageComponentSelected(
    sku: SKUCreateFormData,
    component: PackageSkuComponent
  ): boolean {
    return this.getSelectedPackageComponentsForSku(sku).some(
      (item) => item.packageSkuId === component.packageSkuId
    );
  }

  isAllPackageComponentsSelected(sku: SKUCreateFormData): boolean {
    const components = (sku.skuDetailList || []) as PackageSkuComponent[];
    const selected = this.getSelectedPackageComponentsForSku(sku);
    return components.length > 0 && selected.length === components.length;
  }

  onPackageComponentSelectAll(event: any, sku: SKUCreateFormData): void {
    const checked = event.checked || false;
    const components = (sku.skuDetailList || []) as PackageSkuComponent[];
    this.updateSelectedPackageComponents(
      sku,
      checked ? [...components] : []
    );
  }

  onPackageComponentRowSelectionChange(
    event: any,
    sku: SKUCreateFormData,
    component: PackageSkuComponent
  ): void {
    const checked = event.checked || false;
    const current = this.getSelectedPackageComponentsForSku(sku);
    const next = checked
      ? [
          ...current.filter(
            (item) => item.packageSkuId !== component.packageSkuId
          ),
          component,
        ]
      : current.filter((item) => item.packageSkuId !== component.packageSkuId);

    this.updateSelectedPackageComponents(sku, next);
  }

  removePackageComponent(
    sku: SKUCreateFormData,
    component: PackageSkuComponent
  ): void {
    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title'
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.delete_single',
        {
          skuName: component.skuName || component.skuCode,
        }
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete'
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel'
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.deletePackageComponent(sku, component);
      },
    });
  }

  removeSelectedPackageComponents(sku: SKUCreateFormData): void {
    const selected = this.getSelectedPackageComponentsForSku(sku);
    if (selected.length === 0) {
      return;
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title'
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.delete_multiple',
        {
          count: selected.length,
        }
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete'
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel'
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.deleteSelectedPackageComponents(sku);
      },
    });
  }

  private deletePackageComponent(
    sku: SKUCreateFormData,
    component: PackageSkuComponent
  ): void {
    sku.skuDetailList = ((sku.skuDetailList || []) as PackageSkuComponent[]).filter(
      (item) => item.packageSkuId !== component.packageSkuId
    );
    this.updateSelectedPackageComponents(
      sku,
      this.getSelectedPackageComponentsForSku(sku).filter(
        (item) => item.packageSkuId !== component.packageSkuId
      )
    );
    this.manuallyGeneratedSkus.set([...this.manuallyGeneratedSkus()]);
  }

  private deleteSelectedPackageComponents(sku: SKUCreateFormData): void {
    const selectedIds = new Set(
      this.getSelectedPackageComponentsForSku(sku).map(
        (component) => component.packageSkuId
      )
    );
    sku.skuDetailList = ((sku.skuDetailList || []) as PackageSkuComponent[]).filter(
      (item) => !selectedIds.has(item.packageSkuId)
    );
    this.clearSelectedPackageComponents(sku);
    this.manuallyGeneratedSkus.set([...this.manuallyGeneratedSkus()]);
  }

  private updateSelectedPackageComponents(
    sku: SKUCreateFormData,
    components: PackageSkuComponent[]
  ): void {
    const nextMap = new Map(this.selectedPackageComponentsMap());
    const key = this.getSkuSelectionKey(sku);

    if (components.length === 0) {
      nextMap.delete(key);
    } else {
      nextMap.set(key, components);
    }

    this.selectedPackageComponentsMap.set(nextMap);
  }

  private clearSelectedPackageComponents(sku: SKUCreateFormData): void {
    this.updateSelectedPackageComponents(sku, []);
  }

  private getSkuSelectionKey(sku: SKUCreateFormData): string {
    return String(sku.id || sku.skuCode || sku.spuId || sku.skuName);
  }

  onPackageComponentQuantityChange(sku: SKUCreateFormData): void {
    const components = (sku.skuDetailList || []) as PackageSkuComponent[];
    components.forEach((component) => {
      component.num = Math.max(1, Number(component.num || 1));
    });
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: message,
      life: 5000,
    });
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.messages.success'),
      detail: message,
      life: 3000,
    });
  }

  getAttributeOptions(attribute: ProductAttribute): DropdownOption[] {
    return attribute.attributeValueList.map((val) => ({
      label: val.value,
      value: val.value,
    }));
  }

  isAttributeSelected(attributeCode: string, value: string): boolean {
    const selected = this.selectedProductAttributes();
    const control = selected[attributeCode];
    if (!control) return false;

    const currentValue = control.value;
    if (Array.isArray(currentValue)) {
      return currentValue.includes(value);
    }
    return currentValue === value;
  }

  isRadioButtonSelected(attributeCode: string, value: string): boolean {
    const radioValues = this.radioButtonValues();
    return radioValues[attributeCode] === value;
  }

  onRadioButtonChange(attributeCode: string, value: string): void {
    const currentRadioValues = this.radioButtonValues();
    const updatedRadioValues = {
      ...currentRadioValues,
      [attributeCode]: value,
    };
    this.radioButtonValues.set(updatedRadioValues);

    // Also update the FormControl for consistency
    const attributes = this.selectedProductAttributes();
    if (attributes[attributeCode]) {
      attributes[attributeCode].setValue(value);
    }
  }

  onAttributeValueChange(
    attributeCode: string,
    value: string,
    isMulti: boolean
  ): void {
    const current = this.selectedProductAttributes();
    const control = current[attributeCode];
    if (!control) return;

    let newValue: any;

    if (isMulti) {
      const currentArray = Array.isArray(control.value) ? control.value : [];
      if (currentArray.includes(value)) {
        newValue = currentArray.filter((v) => v !== value);
      } else {
        newValue = [...currentArray, value];
      }
    } else {
      newValue = value;
    }

    control.setValue(newValue);
  }

  getProductSkuAttributeCount(product: ProductInfo): number {
    return this.getSkuGeneratingAttributes(product).length;
  }

  onInputChange(event: Event, rowData: any, prop: string): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;

    if (prop === 'skuCode') {
      // Validate SKU code format - no special characters allowed
      if (this.hasSpecialCharacters(newValue)) {
        // Reset to previous valid value
        target.value = rowData[prop] || '';
        return; // Don't update the value
      }

      // Validate SKU code uniqueness
      const isDuplicate = this.isSkuCodeDuplicate(newValue, rowData);
      if (isDuplicate) {
        // Don't update the value if duplicate
        target.value = rowData[prop] || '';
        return;
      }
    }

    if (prop === 'skuName') {
      // Validate SKU name - cannot be spaces only
      if (this.isSkuNameInvalid(newValue)) {
        // Reset to previous valid value
        target.value = rowData[prop] || '';
        return; // Don't update the value
      }
    }

    rowData[prop] = newValue;
  }

  /**
   * Handles blur event for SKU code input - auto-sanitizes if needed
   * @param event - The blur event
   * @param rowData - The row data object
   */
  onSkuCodeBlur(event: Event, rowData: any): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;

    if (currentValue && this.hasSpecialCharacters(currentValue)) {
      // Auto-sanitize the SKU code
      const sanitizedValue = this.sanitizeSkuCode(currentValue);

      // Check if sanitized value is unique
      if (!this.isSkuCodeDuplicate(sanitizedValue, rowData)) {
        rowData.skuCode = sanitizedValue;
        target.value = sanitizedValue;
        // No toast message needed
      } else {
        // If sanitized value is duplicate, reset without error message
        target.value = rowData.skuCode || '';
      }
    }
  }

  /**
   * Handles blur event for SKU name input - auto-trims spaces
   * @param event - The blur event
   * @param rowData - The row data object
   */
  onSkuNameBlur(event: Event, rowData: any): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;

    if (currentValue) {
      // Auto-trim the SKU name
      const trimmedValue = currentValue.trim();
      rowData.skuName = trimmedValue;
      target.value = trimmedValue;
    }
  }

  /**
   * Handles paste event for SKU code input - validates pasted content
   * @param event - The paste event
   * @param rowData - The row data object
   */
  onSkuCodePaste(event: ClipboardEvent, rowData: any): void {
    const pastedText = event.clipboardData?.getData('text') || '';

    if (this.hasSpecialCharacters(pastedText)) {
      // Prevent paste if it contains special characters
      event.preventDefault();
      // No toast message needed - validation message will show below the field
    }
  }

  /**
   * Checks if a string contains special characters (only allows letters, numbers, hyphens, and underscores)
   * and validates length (maximum 32 characters)
   * @param value - The string to validate
   * @returns True if the string contains special characters, exceeds length limit, or is spaces-only
   */
  hasSpecialCharacters(value: string): boolean {
    if (!value) {
      return false; // Null/undefined values are allowed (handled by required validator)
    }

    const trimmedValue = value.trim();

    // Spaces-only values are invalid
    if (trimmedValue === '') {
      return true;
    }

    // Check length limit (32 characters maximum)
    if (trimmedValue.length > 32) {
      return true;
    }

    // Regular expression to allow only letters, numbers, hyphens, and underscores
    const validSkuCodePattern = /^[a-zA-Z0-9_-]+$/;
    return !validSkuCodePattern.test(trimmedValue);
  }

  /**
   * Sanitizes a SKU code by removing or replacing special characters
   * @param value - The string to validate
   * @returns A sanitized string with only letters, numbers, hyphens, and underscores (max 32 chars)
   */
  private sanitizeSkuCode(value: string): string {
    if (!value) {
      return value;
    }

    // First trim the value
    let sanitized = value.trim();

    if (sanitized === '') {
      return ''; // Return empty string for spaces-only input
    }

    // Replace special characters with hyphens, preserve underscores
    sanitized = sanitized
      .replace(/[^a-zA-Z0-9_-]/g, '-') // Replace special chars with hyphens, keep underscores
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

    // Truncate to 32 characters if too long
    if (sanitized.length > 32) {
      sanitized = sanitized.substring(0, 32);
      // Remove trailing hyphen if truncation created one
      sanitized = sanitized.replace(/-$/, '');
    }

    return sanitized;
  }

  /**
   * Checks if SKU name is invalid (spaces only or empty)
   * @param value - The SKU name value to validate
   * @returns True if the SKU name is invalid
   */
  isSkuNameInvalid(value: string): boolean {
    if (!value) {
      return false; // Null/undefined values are allowed (handled by required validator)
    }

    const trimmedValue = value.trim();

    // Spaces-only values are invalid
    return trimmedValue === '';
  }

  /**
   * Returns CSS classes for SKU code input based on validation state
   * @param value - The SKU code value to validate
   * @returns CSS classes string
   */
  getSkuCodeInputClass(value: string): string {
    const baseClasses = 'w-full text-sm p-2 border rounded transition-colors';

    if (!value || value.trim() === '') {
      return `${baseClasses} border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-1 focus:ring-green-500`;
    }

    if (this.hasSpecialCharacters(value)) {
      return `${baseClasses} border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500`;
    }

    return `${baseClasses} border-green-500 focus:border-green-500 focus:ring-1 focus:ring-green-500`;
  }

  /**
   * Returns CSS classes for SKU name input based on validation state
   * @param value - The SKU name value to validate
   * @returns CSS classes string
   */
  getSkuNameInputClass(value: string): string {
    const baseClasses = 'w-full text-sm p-2 border rounded transition-colors';

    if (!value || value.trim() === '') {
      return `${baseClasses} border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-1 focus:ring-green-500`;
    }

    if (this.isSkuNameInvalid(value)) {
      return `${baseClasses} border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500`;
    }

    return `${baseClasses} border-green-500 focus:border-green-500 focus:ring-1 focus:ring-green-500`;
  }

  /**
   * Gets validation message for SKU code input
   * @param value - The SKU code value to validate
   * @returns Validation message string
   */
  getSkuCodeValidationMessage(value: string): string {
    if (!value) {
      return 'SKU Code is required';
    }

    const trimmedValue = value.trim();
    if (trimmedValue === '') {
      return 'SKU Code cannot be empty or contain only spaces';
    }

    if (trimmedValue.length > 32) {
      return 'SKU Code cannot exceed 32 characters';
    }

    if (this.hasSpecialCharacters(value)) {
      return 'Only letters (A-Z, a-z), numbers (0-9), hyphens (-), and underscores (_) are allowed';
    }

    return '';
  }

  /**
   * Gets validation message for SKU name input
   * @param value - The SKU name value to validate
   * @returns Validation message string
   */
  getSkuNameValidationMessage(value: string): string {
    if (!value) {
      return 'SKU Name is required';
    }

    const trimmedValue = value.trim();
    if (trimmedValue === '') {
      return 'SKU Name cannot be empty or contain only spaces';
    }

    return '';
  }

  /**
   * Checks if barcode is invalid
   * @param value - The barcode value to validate
   * @returns True if barcode is invalid
   */

  /**
   * Gets helpful suggestions for common special character replacements
   * @param value - The SKU code value to analyze
   * @returns Array of suggestion strings
   */
  getSkuCodeSuggestions(value: string): string[] {
    if (!value || !this.hasSpecialCharacters(value)) {
      return [];
    }

    const suggestions: string[] = [];

    // Check length first
    if (value.length > 32) {
      suggestions.push('SKU code is too long. Maximum 32 characters allowed.');
    }

    // Common replacements
    if (value.includes(' ')) {
      suggestions.push(
        'Replace spaces with hyphens (e.g., "Product Name" → "Product-Name")'
      );
    }
    if (value.includes('_')) {
      suggestions.push('Underscores are allowed and can be kept as-is');
    }
    if (value.includes('&')) {
      suggestions.push(
        'Replace & with "and" (e.g., "Product&Service" → "Product-and-Service")'
      );
    }
    if (value.includes('@')) {
      suggestions.push('Remove @ symbol or replace with "at"');
    }
    if (value.includes('#')) {
      suggestions.push('Remove # symbol or replace with "number"');
    }
    if (value.includes('%')) {
      suggestions.push('Remove % symbol or replace with "percent"');
    }

    return suggestions;
  }

  /**
   * Checks if any SKU codes in the current list contain special characters
   * @returns True if any SKU codes are invalid
   */
  hasAnyInvalidSkuCodes(): boolean {
    const allSkus = this.manuallyGeneratedSkus();
    return allSkus.some(
      (sku) => sku.skuCode && this.hasSpecialCharacters(sku.skuCode)
    );
  }

  /**
   * Sanitizes all SKU codes in the current list
   * @returns Number of SKUs that were sanitized
   */
  sanitizeAllSkuCodes(): number {
    const allSkus = this.manuallyGeneratedSkus();
    let sanitizedCount = 0;

    allSkus.forEach((sku) => {
      if (sku.skuCode && this.hasSpecialCharacters(sku.skuCode)) {
        const originalCode = sku.skuCode;
        const sanitizedCode = this.sanitizeSkuCode(originalCode);

        // Check if sanitized code is unique
        if (!this.isSkuCodeDuplicate(sanitizedCode, sku)) {
          sku.skuCode = sanitizedCode;
          sanitizedCount++;
        }
      }
    });

    // No toast message needed

    return sanitizedCount;
  }

  /**
   * Checks if a SKU code is duplicate (excluding the current row)
   * @param skuCode - The SKU code to check
   * @param currentRow - The current row being edited
   * @returns True if the SKU code is duplicate
   */
  private isSkuCodeDuplicate(skuCode: string, currentRow: any): boolean {
    if (!skuCode || skuCode.trim() === '') {
      return false; // Empty codes are allowed (will be validated on save)
    }

    const allSkus = this.manuallyGeneratedSkus();
    return allSkus.some(
      (sku) =>
        sku !== currentRow && // Exclude current row
        sku.skuCode &&
        sku.skuCode.trim().toLowerCase() === skuCode.trim().toLowerCase()
    );
  }

  /**
   * Finds existing SKUs that match a given attribute combination
   */
  private findExistingSkusWithCombination(
    combination: { [key: string]: any },
    skuAttributes: ProductAttribute[],
    allExistingSkus: SKUCreateFormData[]
  ): SKUCreateFormData[] {
    const combinationKey = this.createAttributeCombinationKey(
      combination,
      skuAttributes
    );

    if (!combinationKey) return [];

    return allExistingSkus.filter((sku) => {
      const skuKey = this.createAttributeCombinationKey(sku, skuAttributes);
      return skuKey === combinationKey;
    });
  }

  // Selection methods for the new table
  isAllSelected(): boolean {
    const skus = this.manuallyGeneratedSkus();
    const selected = this.selectedSkus();
    return skus.length > 0 && selected.length === skus.length;
  }

  onSelectAll(event: any): void {
    if (event.checked) {
      this.selectedSkus.set([...this.manuallyGeneratedSkus()]);
    } else {
      this.selectedSkus.set([]);
    }
  }

  isRowSelected(rowData: any): boolean {
    return this.selectedSkus().some((sku) => sku === rowData);
  }

  onRowSelectionChange(event: any, rowData: any): void {
    // Prevent selection of read-only SKUs
    if (rowData.isReadOnly) {
      return;
    }

    const currentSelection = this.selectedSkus();
    if (event.checked) {
      if (!currentSelection.some((sku) => sku === rowData)) {
        this.selectedSkus.set([...currentSelection, rowData]);
      }
    } else {
      this.selectedSkus.set(currentSelection.filter((sku) => sku !== rowData));
    }
  }

  /**
   * Check if a SKU row is selectable (not read-only)
   */
  isRowSelectable(rowData: any): boolean {
    return !rowData.isReadOnly;
  }

  // Product selection modal methods
  private getProductTableColumns(): TableColumn[] {
    return [
      {
        prop: 'spuCode',
        label: 'sku_management.create.product_code',
        width: 150,
        type: 'text',
      },
      {
        prop: 'spuName',
        label: 'sku_management.create.product_name',
        width: 250,
        type: 'text',
      },
      {
        prop: 'categoryName',
        label: 'sku_management.create.product_category',
        width: 200,
        type: 'text',
      },
    ];
  }

  onProductSearch(): void {
    this.productCurrentPage.set(1); // Reset to first page
    this.loadProductsForModal();
  }

  onProductSearchReset(): void {
    this.productSearchForm.productCode = '';
    this.productSearchForm.productName = '';
    this.productCurrentPage.set(1); // Reset to first page
    this.loadProductsForModal(); // Reload products with reset filters
  }

  onProductPageChange(event: any): void {
    const newPage = Math.floor(event.first / this.productPageSize()) + 1;
    this.productCurrentPage.set(newPage);
    this.loadProductsForModal();
  }

  onProductTableSelectionChange(selection: any): void {
    // Cast to ProductInfo[] array
    const selectedItems = (selection as ProductInfo[]) || [];
    // Enforce single selection - only keep the last selected item
    if (selectedItems.length > 1) {
      // If multiple items are selected, only keep the last one
      const lastSelected = selectedItems[selectedItems.length - 1];
      this.selectedProductForTable.set(lastSelected);
    } else {
      // For single selection, take the first (and only) selected item
      this.selectedProductForTable.set(
        selectedItems.length > 0 ? selectedItems[0] : null
      );
    }
  }

  onProductSelectionCancel(): void {
    this.showProductSelector.set(false);
    this.selectedProductForTable.set(null);
    // Clear the product options when modal is closed
    this.productOptions.set([]);
  }

  onProductSelectionConfirm(): void {
    const selected = this.selectedProductForTable();
    if (selected) {
      this.loading.set(true);
      this.loadProductDetails(selected.id).subscribe({
        next: (productDetails) => {
          if (this.isPackageMode()) {
            this.onProductSelected(productDetails);
            this.loading.set(false);
            return;
          }

          // Check if the product has attributes and SKU attributes (excluding System Attributes)
          if (
            !productDetails.attributeGroups ||
            productDetails.attributeGroups.length === 0
          ) {
            this.showError(
              this.translateService.instant(
                'sku_management.messages.product_no_attributes_found'
              )
            );
            this.loading.set(false);
            return; // Don't close the modal
          }

          const hasSkuAttributes =
            this.getSkuGeneratingAttributes(productDetails).length > 0;

          if (!hasSkuAttributes) {
            this.showError(
              this.translateService.instant(
                'sku_management.messages.product_no_sku_attributes_found'
              )
            );
            this.loading.set(false);
            return; // Don't close the modal
          }

          // Product can be used for SKU creation
          this.onProductSelected(productDetails);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading product details:', error);
          this.showError(
            this.translateService.instant(
              'sku_management.messages.error_loading_product_details'
            )
          );
          this.loading.set(false);
        },
      });
    }
  }

  // Row expansion methods
  isRowExpanded(rowData: any): boolean {
    return this.expandedRows().has(rowData);
  }

  toggleRowExpansion(rowData: any): void {
    const currentExpanded = this.expandedRows();
    const newExpanded = new Set(currentExpanded);

    if (newExpanded.has(rowData)) {
      newExpanded.delete(rowData);
    } else {
      newExpanded.add(rowData);
    }

    this.expandedRows.set(newExpanded);

    // Update the allRowsExpanded state based on current expansion
    const allSkus = this.manuallyGeneratedSkus();
    this.allRowsExpanded.set(
      newExpanded.size === allSkus.length && allSkus.length > 0
    );
  }

  getSkuAttributes(rowData: any): { key: string; value: string }[] {
    const product = this.selectedProduct();

    // For generated SKUs from API, use the skuAttributeList
    if (rowData.isGenerated && rowData.skuAttributeList) {
      const skuAttributeNames = new Set(
        product
          ? this.getSkuGeneratingAttributes(product).map(
              (attribute) => attribute.name
            )
          : []
      );

      return rowData.skuAttributeList
        .filter((attr: any) => {
          const attributeName = attr.attributeName || attr.name;
          return (
            skuAttributeNames.size === 0 || skuAttributeNames.has(attributeName)
          );
        })
        .map((attr: any) => ({
          key: attr.attributeName || attr.name || 'Unknown',
          value: attr.attributeValue || attr.value || '',
        }));
    }

    // For manually created SKUs, use the product attributes
    if (!product) return [];

    return product.attributeGroups
      .flatMap((group) => group.attributes)
      .filter(
        (attr) => this.isSkuGeneratingAttribute(attr) && rowData[attr.code]
      )
      .map((attr) => ({
        key: attr.name,
        value: rowData[attr.code],
      }));
  }

  getDisplayAttributes(rowData: any): { key: string; value: string }[] {
    if (!this.isPackageMode()) {
      return this.getSkuAttributes(rowData);
    }

    return this.getPackageProductAttributes();
  }

  private getPackageProductAttributes(): { key: string; value: string }[] {
    const product = this.selectedProduct();
    if (!product?.attributeGroups?.length) {
      return [];
    }

    return product.attributeGroups
      .flatMap((group) => group.attributes)
      .filter((attr) => !attr.skuFlag)
      .map((attr) => ({
        key: attr.name,
        value: this.getAttributeValuesText(attr),
      }))
      .filter((attr) => attr.key && attr.value);
  }

  private getAttributeValuesText(attribute: ProductAttribute): string {
    return (attribute.attributeValueList || [])
      .map((value) => value.value || value.name)
      .filter((value) => !!value)
      .join(', ');
  }

  expandAllRows(): void {
    const skus = this.manuallyGeneratedSkus();
    this.expandedRows.set(new Set(skus));
    this.allRowsExpanded.set(true);
  }

  collapseAllRows(): void {
    this.expandedRows.set(new Set());
    this.allRowsExpanded.set(false);
  }

  /**
   * Get status text for SKU
   */
  getSkuStatusText(status: number): string {
    switch (status) {
      case 0:
        return this.translateService.instant('sku_management.status.draft');
      case 1:
        return this.translateService.instant('sku_management.status.rejected');
      case 2:
        return this.translateService.instant(
          'sku_management.status.under_review'
        );
      case 3:
        return this.translateService.instant('sku_management.status.effective');
      case 4:
        return this.translateService.instant(
          'sku_management.status.effective_editing'
        );
      case 5:
        return this.translateService.instant(
          'sku_management.status.effective_rejected'
        );
      default:
        return this.translateService.instant('common.messages.unknown');
    }
  }

  /**
   * Get status severity for PrimeNG tag
   */
  getSkuStatusSeverity(status: number): string {
    switch (status) {
      case 0:
        return 'info';
      case 1:
        return 'danger';
      case 2:
        return 'warning';
      case 3:
        return 'success';
      case 4:
        return 'warning';
      case 5:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  /**
   * Get status style for consistent styling
   */
  getSkuStatusStyle(status: number): any {
    switch (status) {
      case 0: // Draft
        return {
          'background-color': '#eff6ff',
          color: '#2563eb',
          border: 'none',
        };
      case 1: // Rejected
        return {
          'background-color': '#fef2f2',
          color: '#dc2626',
          border: 'none',
        };
      case 2: // Under Review
        return {
          'background-color': '#fef3c7',
          color: '#a16207',
          border: 'none',
        };
      case 3: // Effective
        return {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        };
      case 4: // Effective - Editing
        return {
          'background-color': '#fef3c7',
          color: '#a16207',
          border: 'none',
        };
      case 5: // Effective - Rejected
        return {
          'background-color': '#fef2f2',
          color: '#dc2626',
          border: 'none',
        };
      default:
        return {};
    }
  }

  // ===== BULK APPLY METHODS =====

  /**
   * Apply weight to all SKUs
   */
  applyWeightToAll(value: number, currentSku?: SKUCreateFormData): void {
    this.currentEditingSku.set(currentSku || null);
    this.showBulkApplyConfirmation(
      'weight',
      value,
      this.translateService.instant('sku_management.messages.weight')
    );
  }

  /**
   * Apply length to all SKUs
   */
  applyLengthToAll(value: number, currentSku?: SKUCreateFormData): void {
    this.currentEditingSku.set(currentSku || null);
    this.showBulkApplyConfirmation(
      'length',
      value,
      this.translateService.instant('sku_management.messages.length')
    );
  }

  /**
   * Apply width to all SKUs
   */
  applyWidthToAll(value: number, currentSku?: SKUCreateFormData): void {
    this.currentEditingSku.set(currentSku || null);
    this.showBulkApplyConfirmation(
      'width',
      value,
      this.translateService.instant('sku_management.messages.width')
    );
  }

  /**
   * Apply height to all SKUs
   */
  applyHeightToAll(value: number, currentSku?: SKUCreateFormData): void {
    this.currentEditingSku.set(currentSku || null);
    this.showBulkApplyConfirmation(
      'height',
      value,
      this.translateService.instant('sku_management.messages.height')
    );
  }

  /**
   * Apply barcode to all SKUs
   */
  applyBarcodeToAll(value: string, currentSku?: SKUCreateFormData): void {
    this.currentEditingSku.set(currentSku || null);
    this.showBulkApplyConfirmation(
      'barcode',
      value,
      this.translateService.instant('sku_management.messages.barcode')
    );
  }

  /**
   * Apply all system attributes fields to all SKUs
   */
  applyAllSystemAttributesToAll(currentSku?: SKUCreateFormData): void {
    this.currentEditingSku.set(currentSku || null);
    this.showBulkApplySectionConfirmation(
      'systemAttributes',
      this.translateService.instant('sku_management.messages.system_attributes')
    );
  }

  applyTaxToAll(currentSku: SKUCreateFormData): void {
    this.currentEditingSku.set(currentSku);
    this.showBulkApplySectionConfirmation(
      'taxConfiguration',
      this.translateService.instant('goods.tax.section_title')
    );
  }

  onSkuTaxValueChange(sku: SKUCreateFormData, value: ProductTaxValue): void {
    sku.taxValue = value;
    this.manuallyGeneratedSkus.set([...this.manuallyGeneratedSkus()]);
  }

  /**
   * Show bulk apply confirmation dialog
   */
  private showBulkApplyConfirmation(
    property: string,
    value: number | string,
    fieldName: string
  ): void {
    const availableSkus = this.availableSkusForBulkApply();
    const affectedCount = availableSkus.length;

    if (affectedCount === 0) {
      this.showError('No other SKUs to apply this value to');
      return;
    }

    // Set dialog data
    this.bulkApplyProperty.set(property);
    this.bulkApplyValue.set(value);
    this.bulkApplyFieldName.set(fieldName);
    this.bulkApplyCount.set(affectedCount);

    // Initialize SKU selection state
    this.bulkApplySelectedSkus.set([]);
    this.bulkApplyAllSkusSelected.set(false);

    // Show SKU selection dialog first
    this.showSkuSelectionDialog.set(true);
  }

  /**
   * Show bulk apply confirmation dialog for entire sections
   */
  private showBulkApplySectionConfirmation(
    sectionType: 'systemAttributes' | 'taxConfiguration',
    sectionName: string
  ): void {
    const availableSkus = this.availableSkusForBulkApply();
    const affectedCount = availableSkus.length;

    if (affectedCount === 0) {
      this.showError('No other SKUs to apply this section to');
      return;
    }

    // Set dialog data for section apply
    this.bulkApplyProperty.set(sectionType);
    this.bulkApplyValue.set(null); // No single value for sections
    this.bulkApplyFieldName.set(sectionName);
    this.bulkApplyCount.set(affectedCount);

    // Initialize SKU selection state
    this.bulkApplySelectedSkus.set([]);
    this.bulkApplyAllSkusSelected.set(false);

    // Show SKU selection dialog first
    this.showSkuSelectionDialog.set(true);
  }

  /**
   * Cancel bulk apply operation
   */
  cancelBulkApply(): void {
    this.showBulkApplyDialog.set(false);
    this.showSkuSelectionDialog.set(false);
    this.bulkApplyProperty.set('');
    this.bulkApplyValue.set(null);
    this.bulkApplyFieldName.set('');
    this.bulkApplyCount.set(0);
    this.bulkApplySelectedSkus.set([]);
    this.bulkApplyAllSkusSelected.set(false);
    this.currentEditingSku.set(null);
  }

  /**
   * Confirm and execute bulk apply operation
   */
  confirmBulkApply(): void {
    const property = this.bulkApplyProperty();
    const value = this.bulkApplyValue();
    const fieldName = this.bulkApplyFieldName();
    const selectedSkus = this.bulkApplySelectedSkus();

    if (!property) {
      this.showError('Invalid bulk apply operation');
      return;
    }

    // For section-based operations, value can be null
    if (
      value === null &&
      property !== 'systemAttributes' &&
      property !== 'taxConfiguration'
    ) {
      this.showError('Invalid bulk apply operation');
      return;
    }

    if (selectedSkus.length === 0) {
      this.showError('Please select at least one SKU to apply the value to');
      return;
    }

    // Apply the value/section to selected SKUs using the unified method
    this.applyValueToAllSkus(property, value, fieldName, selectedSkus);

    // Close dialog and reset state
    this.cancelBulkApply();
  }

  /**
   * Generic method to apply a value to all SKUs (kept for backward compatibility)
   */
  private applyValueToAllSkus(
    property: string,
    value: number | string | null,
    fieldName: string,
    targetSkus?: SKUCreateFormData[]
  ): void {
    const skus = targetSkus || this.manuallyGeneratedSkus();
    const affectedCount = skus.length;

    if (affectedCount <= 0) {
      this.showError('No other SKUs to apply this value to');
      return;
    }

    // Handle section-based application
    if (
      property === 'systemAttributes' ||
      property === 'taxConfiguration'
    ) {
      this.applySectionToAllSkus(property, skus);
      return;
    }

    // Apply the single value to all SKUs
    skus.forEach((sku) => {
      (sku as any)[property] = value;
    });

    const successMessage = this.translateService.instant(
      affectedCount > 1
        ? 'sku_management.messages.field_applied_success_plural'
        : 'sku_management.messages.field_applied_success',
      { fieldName, count: affectedCount }
    );
    this.showSuccess(successMessage);
  }

  /**
   * Apply entire section to all SKUs
   */
  private applySectionToAllSkus(
    sectionType: 'systemAttributes' | 'taxConfiguration',
    targetSkus: SKUCreateFormData[]
  ): void {
    const currentSku = this.currentEditingSku();
    if (!currentSku) {
      this.showError('No source SKU found to copy from');
      return;
    }

    const affectedCount = targetSkus.length;
    let appliedFields: string[] = [];

    // Apply all fields from the section
    targetSkus.forEach((sku) => {
      if (sectionType === 'systemAttributes') {
        // Copy all system attributes fields
        sku.weight = currentSku.weight;
        sku.length = currentSku.length;
        sku.width = currentSku.width;
        sku.height = currentSku.height;

        if (appliedFields.length === 0) {
          appliedFields = ['Weight', 'Length', 'Width', 'Height'];
        }
      } else if (sectionType === 'taxConfiguration') {
        sku.taxValue = {
          ...(currentSku.taxValue ?? DEFAULT_PRODUCT_TAX_VALUE),
        };
        sku.inheritedTaxValue = currentSku.inheritedTaxValue ?? null;
      }
    });

    // Use section name instead of listing all fields
    const sectionName =
      sectionType === 'taxConfiguration'
        ? this.translateService.instant('goods.tax.section_title')
        : this.translateService.instant(
            'sku_management.messages.system_attributes'
          );

    const successMessage = this.translateService.instant(
      affectedCount > 1
        ? 'sku_management.messages.section_applied_success_plural'
        : 'sku_management.messages.section_applied_success',
      { sectionName, count: affectedCount }
    );
    this.showSuccess(successMessage);
  }

  // ===== SKU SELECTION FOR BULK APPLY METHODS =====

  /**
   * Handle "Select All" checkbox in SKU selection dialog
   */
  onBulkApplySelectAll(event: any): void {
    if (event.checked) {
      this.bulkApplySelectedSkus.set([...this.availableSkusForBulkApply()]);
      this.bulkApplyAllSkusSelected.set(true);
    } else {
      this.bulkApplySelectedSkus.set([]);
      this.bulkApplyAllSkusSelected.set(false);
    }
  }

  /**
   * Check if all SKUs are selected in bulk apply dialog
   */
  isBulkApplyAllSelected(): boolean {
    const skus = this.availableSkusForBulkApply();
    const selected = this.bulkApplySelectedSkus();
    return skus.length > 0 && selected.length === skus.length;
  }

  /**
   * Handle individual SKU selection in bulk apply dialog
   */
  onBulkApplySkuSelectionChange(event: any, sku: SKUCreateFormData): void {
    const currentSelection = this.bulkApplySelectedSkus();

    if (event.checked) {
      if (!currentSelection.some((selectedSku) => selectedSku === sku)) {
        this.bulkApplySelectedSkus.set([...currentSelection, sku]);
      }
    } else {
      this.bulkApplySelectedSkus.set(
        currentSelection.filter((selectedSku) => selectedSku !== sku)
      );
    }

    // Update "Select All" state
    const skus = this.availableSkusForBulkApply();
    const newSelection = this.bulkApplySelectedSkus();
    this.bulkApplyAllSkusSelected.set(
      newSelection.length === skus.length && skus.length > 0
    );
  }

  /**
   * Check if a specific SKU is selected in bulk apply dialog
   */
  isBulkApplySkuSelected(sku: SKUCreateFormData): boolean {
    return this.bulkApplySelectedSkus().some(
      (selectedSku) => selectedSku === sku
    );
  }

  /**
   * Proceed to confirmation dialog after SKU selection
   */
  proceedToBulkApplyConfirmation(): void {
    const selectedSkus = this.bulkApplySelectedSkus();

    if (selectedSkus.length === 0) {
      this.showError('Please select at least one SKU to apply the value to');
      return;
    }

    // Update the count to reflect selected SKUs
    this.bulkApplyCount.set(selectedSkus.length);

    // Close SKU selection dialog and show confirmation dialog
    this.showSkuSelectionDialog.set(false);
    this.showBulkApplyDialog.set(true);
  }

  /**
   * Cancel SKU selection and return to normal flow
   */
  cancelSkuSelection(): void {
    this.showSkuSelectionDialog.set(false);
    this.bulkApplySelectedSkus.set([]);
    this.bulkApplyAllSkusSelected.set(false);
    this.currentEditingSku.set(null);
  }

  // Barcode management methods for each SKU
  getBarcodeListForSku(sku: SKUCreateFormData): Barcode[] {
    const skuKey = sku.skuCode || sku.id?.toString() || '';
    // Access the signal to trigger change detection
    const map = this.skuBarcodeMap();
    return map.get(skuKey) || [];
  }

  getSelectedBarcodesForSku(sku: SKUCreateFormData): Barcode[] {
    const skuKey = sku.skuCode || sku.id?.toString() || '';
    return this.selectedBarcodesMap().get(skuKey) || [];
  }

  onAddBarcode(sku: SKUCreateFormData): void {
    this.editingBarcode.set({ sku, barcode: null });
    this.showBarcodeDialog.set(true);
  }

  onEditBarcode(sku: SKUCreateFormData, barcode: Barcode): void {
    this.editingBarcode.set({ sku, barcode });
    this.showBarcodeDialog.set(true);
  }

  onDeleteBarcode(sku: SKUCreateFormData, barcode: Barcode): void {
    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title'
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.delete_single',
        {
          skuName: barcode.barcodeValue,
        }
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete'
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel'
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.deleteBarcode(sku, barcode);
      },
    });
  }

  private deleteBarcode(sku: SKUCreateFormData, barcode: Barcode): void {
    const skuKey = sku.skuCode || sku.id?.toString() || '';
    this.skuBarcodeMap.update((map) => {
      const newMap = new Map(map);
      const currentList = newMap.get(skuKey) || [];
      newMap.set(
        skuKey,
        currentList.filter((b) => b.id !== barcode.id)
      );
      return newMap;
    });
    // Also update the SKU's barcodes property
    if (sku.barcodes) {
      sku.barcodes = sku.barcodes.filter((b) => b.id !== barcode.id);
    }
  }

  onDeleteSelectedBarcodes(sku: SKUCreateFormData): void {
    const skuKey = sku.skuCode || sku.id?.toString() || '';
    const selected = this.getSelectedBarcodesForSku(sku);
    if (selected.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'sku_management.barcode.messages.no_barcodes_selected'
        ),
      });
      return;
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title'
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.delete_multiple',
        {
          count: selected.length,
        }
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete'
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel'
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.deleteSelectedBarcodes(sku, selected);
      },
    });
  }

  private deleteSelectedBarcodes(
    sku: SKUCreateFormData,
    selected: Barcode[]
  ): void {
    const skuKey = sku.skuCode || sku.id?.toString() || '';
    const selectedIds = new Set(selected.map((b) => b.id));
    this.skuBarcodeMap.update((map) => {
      const newMap = new Map(map);
      const currentList = newMap.get(skuKey) || [];
      newMap.set(
        skuKey,
        currentList.filter((b) => !selectedIds.has(b.id))
      );
      return newMap;
    });
    
    // Also update the SKU's barcodes property
    if (sku.barcodes) {
      sku.barcodes = sku.barcodes.filter((b) => !selectedIds.has(b.id));
    }
    
    this.selectedBarcodesMap.update((map) => {
      const newMap = new Map(map);
      newMap.set(skuKey, []);
      return newMap;
    });
  }

  onBarcodeDialogConfirm(barcodeData: Barcode): void {
    const editing = this.editingBarcode();
    if (!editing) return;

    const { sku, barcode: existingBarcode } = editing;
    const skuKey = sku.skuCode || sku.id?.toString() || '';

    // Generate ID once for consistency
    const barcodeId = existingBarcode ? existingBarcode.id : this.nextBarcodeId();
    if (!existingBarcode) {
      this.nextBarcodeId.update((id) => id + 1);
    }

    const newBarcode: Barcode = {
      ...barcodeData,
      id: barcodeId,
    };

    this.skuBarcodeMap.update((map) => {
      const newMap = new Map(map);
      const currentList = newMap.get(skuKey) || [];

      if (existingBarcode) {
        const updatedList = currentList.map((b) =>
          b.id === existingBarcode.id ? newBarcode : b
        );
        newMap.set(skuKey, updatedList);
      } else {
        const newList = [...currentList, newBarcode];
        newMap.set(skuKey, newList);
      }
      
      return newMap;
    });

    // Also update the SKU's barcodes property
    if (!sku.barcodes) {
      sku.barcodes = [];
    }
    if (existingBarcode) {
      sku.barcodes = sku.barcodes.map((b) =>
        b.id === existingBarcode.id ? newBarcode : b
      );
    } else {
      sku.barcodes = [...sku.barcodes, newBarcode];
    }

    // Force change detection to update the UI
    this.cdr.markForCheck();

    this.showBarcodeDialog.set(false);
    this.editingBarcode.set(null);
  }

  onBarcodeSelectionChange(sku: SKUCreateFormData, selected: Barcode[]): void {
    const skuKey = sku.skuCode || sku.id?.toString() || '';
    this.selectedBarcodesMap.update((map) => {
      const newMap = new Map(map);
      newMap.set(skuKey, selected);
      return newMap;
    });
  }

  isAllBarcodesSelectedForSku(sku: SKUCreateFormData): boolean {
    const list = this.getBarcodeListForSku(sku);
    return list.length > 0 && this.getSelectedBarcodesForSku(sku).length === list.length;
  }

  onSelectAllBarcodes(sku: SKUCreateFormData, event: any): void {
    const skuKey = sku.skuCode || sku.id?.toString() || '';
    if (event.checked) {
      this.selectedBarcodesMap.update((map) => {
        const newMap = new Map(map);
        newMap.set(skuKey, [...this.getBarcodeListForSku(sku)]);
        return newMap;
      });
    } else {
      this.selectedBarcodesMap.update((map) => {
        const newMap = new Map(map);
        newMap.set(skuKey, []);
        return newMap;
      });
    }
  }

  isBarcodeRowSelected(sku: SKUCreateFormData, barcode: Barcode): boolean {
    return this.getSelectedBarcodesForSku(sku).some((b) => b.id === barcode.id);
  }

  onBarcodeRowSelectionChange(
    sku: SKUCreateFormData,
    barcode: Barcode,
    event: any
  ): void {
    const skuKey = sku.skuCode || sku.id?.toString() || '';
    if (event.checked) {
      this.selectedBarcodesMap.update((map) => {
        const newMap = new Map(map);
        const currentList = newMap.get(skuKey) || [];
        newMap.set(skuKey, [...currentList, barcode]);
        return newMap;
      });
    } else {
      this.selectedBarcodesMap.update((map) => {
        const newMap = new Map(map);
        const currentList = newMap.get(skuKey) || [];
        newMap.set(
          skuKey,
          currentList.filter((b) => b.id !== barcode.id)
        );
        return newMap;
      });
    }
  }

}
